import {compact} from '@benchling/js-utils';
import React, {useMemo} from 'react';

import {getPurposesForFolderSection} from 'archive/ArchivePurposesUtil';
import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import {StringIDFolder} from 'constants/FolderConstants';
import * as NavConstants from 'constants/NavConstants';
import {getRegistrySupportedOligoAwareTypes, RegistrySummary} from 'constants/RegistryConstants';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import ItemListing from 'nav/sidepanel/listing/ItemListing';
import RegistryCreateMenu from 'registry/sidepanel/RegistryCreateMenu';
import {Schema} from 'schemas/SchemaConstants';
import {TypesFilterSchemasQuery} from 'search/filters/__generated/TypesFilterSchemasQuery';
import {FilterOption} from 'search/filters/base/FilterComponents';
import * as FilterUtil from 'search/FilterUtil';
import {FilterKey, SearchParams, SingleValueFilter, SortKey} from 'search/SearchConstants';
import {SearchProps} from 'search/withSearching';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import isModelTypeEnabledForListing from 'util/isModelTypeEnabledForListing';
import preserveShallowEquality from 'util/preserveShallowEquality';

import {PresentationalFolderListingCrumbs} from './FolderListingCrumbs';
import {ItemListingSchemas, ItemListingSchemasByType, SchemasFilterFunction} from './ItemListingUtil';

type ItemListingTagSchema = ItemListingSchemas['tagSchemas'][number];
type TypesFilterTagSchema = TypesFilterSchemasQuery['tagSchemas'][number];

const DEFAULT_TYPES = getRegistrySupportedOligoAwareTypes().filter((type) =>
  isModelTypeEnabledForListing(type)
);
const PRESERVED_FILTER_KEYS = [
  FilterKey.FOLDER,
  FilterKey.FOLDER_SECTION,
  FilterKey.REGISTRY_COLLECTION_OWNER,
];

export interface BioentityListingProps {
  activeItemId: string | null;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  isExpanded: boolean;
  folder: StringIDFolder;
  onNavigateToRoot: () => void;
}

interface EnhancedProps {
  registry: RegistrySummary;
}

class BioentityListingConnector extends DataConnector<BioentityListingProps & SearchProps, EnhancedProps> {
  enhanceProps: () => EnhancedProps;

  constructor(props: BioentityListingProps & SearchProps) {
    super(props);
    this.enhanceProps = preserveShallowEquality(this.uncachedEnhanceProps);
  }

  private currentRegistry(): RegistrySummary {
    return {...(this.props.folder.source as RegistrySummary), rootFolder: this.props.folder};
  }

  private uncachedEnhanceProps = (): EnhancedProps => {
    const registry = this.currentRegistry();
    return {registry};
  };
}

function getSchemasRegistryFilter(registryId: string): SchemasFilterFunction {
  return (schemasByAvailableType: ItemListingSchemasByType): ItemListingSchemasByType => {
    const filteredSchemas: ItemListingSchemasByType = {};
    const allTagSchemaIds: Set<string> = new Set<string>();
    for (const type of Object.keys(schemasByAvailableType)) {
      const tagSchemas = schemasByAvailableType[type] as Array<ItemListingTagSchema | TypesFilterTagSchema>;
      const typeRegistrySchemas = tagSchemas.filter((schema) => schema.registry?.id === registryId);
      filteredSchemas[type] = typeRegistrySchemas;
      typeRegistrySchemas.forEach((schema) => allTagSchemaIds.add(schema.id));
    }

    return filteredSchemas;
  };
}

export function BioentityListing(props: BioentityListingProps & EnhancedProps & SearchProps): JSX.Element {
  const registry = props.registry;
  const folder: StringIDFolder = {
    ...registry.rootFolder,
    source: registry,
    id: registry.rootFolder.api_identifier,
  };

  const schemasRegistryFilter: SchemasFilterFunction = useMemo(
    () => getSchemasRegistryFilter(registry.id),
    [registry.id]
  );

  const folderFilter = FilterUtil.getFilterByKey(
    props.searchParams.filters,
    FilterKey.FOLDER
  ) as SingleValueFilter;

  // This should only happen in transitionary states where we are switching between registries, and the
  // component re-renders in between steps of SidepanelReduxActions.listFolder. If this is a persistent
  // state, this reflects a bug.
  if (!folderFilter || props.folder.api_identifier !== folderFilter.value) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <div className="sidepanel">
      <ItemListing
        {...props}
        actionSource={ActionOrigin.REGISTRY_EXPANDED_SEARCH}
        savedSearchSection={SearchSection.REGISTRIES}
        availableTypes={getRegistrySupportedOligoAwareTypes().filter((type) =>
          isModelTypeEnabledForListing(type)
        )}
        defaultTypes={DEFAULT_TYPES}
        // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
        makeCrumbs={(settingsComponents?: JSX.Element) => (
          <PresentationalFolderListingCrumbs
            // This folder is always readable since it is the folder (or registry) that the user is currently
            // listing.
            ancestralFolders={[{...folder, permissions: {...folder.permissions!, readable: true}}]}
            onNavigateToRoot={props.onNavigateToRoot}
            onOpenFolder={(folder: StringIDFolder, searchParams: SearchParams) => {
              // Since there aren't actually nested folders in the registry, we don't need to set the folder
              // nav section. The only reason this would get called is if a saved search is loaded, and the
              // user clicked the crumb for the registry they are in. In that case, we know we are staying in
              // the same registry as the saved search, so no nav updates are needed.
              props.onSelectSavedSearch(undefined);
              props.onSearch(searchParams);
            }}
            searchParams={props.searchParams}
            sectionName="Registries"
            activeSavedSearch={props.activeSavedSearch}
            settingsComponents={settingsComponents}
          />
        )}
        additionalHeaderButtons={<RegistryCreateMenu dropLeft={props.isExpanded} folder={folder} />}
        extraPrimaryFilterOptions={
          compact([
            !registry.disablePersonalCollections
              ? {
                  filterKey: FilterKey.REGISTRY_COLLECTION_OWNER,
                  extraProps: {registry},
                }
              : null,
          ]) satisfies Array<FilterOption>
        }
        getSecondaryFilterGroups={(selectedSchema: Schema | null) => [
          {
            name: 'General',
            filterOptions: compact([
              {filterKey: FilterKey.NAMES},
              {filterKey: FilterKey.CREATED_AT},
              {filterKey: FilterKey.MODIFIED_AT},
              {filterKey: FilterKey.API_IDS},
              {
                filterKey: FilterKey.ARCHIVE_PURPOSES,
                extraProps: {
                  archivePurposes: getPurposesForFolderSection(FileBrowserConstants.INVENTORY_SECTION),
                },
              },
              {
                filterKey: FilterKey.SCHEMA_FIELD,
                extraProps: {schemaFields: selectedSchema ? selectedSchema.fields : []},
              },
              !BenchlingConfig.ENABLE_AUTHORS ? {filterKey: FilterKey.CREATOR} : null,
              {filterKey: FilterKey.PROJECT_FOLDER},
              {filterKey: FilterKey.NOTE_REFERENCE},
            ]) satisfies Array<FilterOption>,
          },
          {
            name: 'Entity',
            filterOptions: compact([
              {filterKey: FilterKey.BIOENTITY_LABELS},
              BenchlingConfig.ENABLE_AUTHORS ? {filterKey: FilterKey.AUTHOR} : null,
              {filterKey: FilterKey.CUSTOM_TAG},
              {filterKey: FilterKey.ANNOTATION},
              {filterKey: FilterKey.SEQUENCE_BASES},
              BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES
                ? {filterKey: FilterKey.OLIGO_HELM_DNA}
                : null,
              BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES
                ? {filterKey: FilterKey.OLIGO_HELM_RNA}
                : null,
              BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? {filterKey: FilterKey.MONOMER} : null,
              {filterKey: FilterKey.PROTEIN_AMINO_ACIDS},
              isFeatureEnabled({feature: RegistryFeature.REGISTRY_MIXTURES})
                ? {
                    filterKey: FilterKey.INGREDIENTS,
                  }
                : null,
              {filterKey: FilterKey.PART_SEQUENCE},
              {filterKey: FilterKey.RNA_PART_SEQUENCE},
              {filterKey: FilterKey.TRANSCRIPTIONS},
              BenchlingConfig.MOLBIO_ENABLE_ALPHAFOLD_GA ? {filterKey: FilterKey.ALPHAFOLD_STRUCTURE} : null,
            ]) satisfies Array<FilterOption>,
          },
          {
            name: 'Entity Validation',
            filterOptions: [
              {
                filterKey: FilterKey.SCHEMA_VALIDATION_STATUS,
              },
              {
                filterKey: FilterKey.SCHEMA_VALIDATION_ERROR_LABEL,
              },
              {
                filterKey: FilterKey.REGISTRATION_STATUS,
              },
            ] satisfies Array<FilterOption>,
          },
        ]}
        availableSortKeys={[
          SortKey.SORT_REGISTRY_IDENTIFIER_KEY,
          SortKey.SORT_NAME_KEY,
          SortKey.SORT_MODIFIED_AT_KEY,
          SortKey.SORT_CREATED_AT_KEY,
        ]}
        preservedFilterKeys={PRESERVED_FILTER_KEYS}
        section={NavConstants.Section.REGISTRIES}
        dataTestAttrs={{component: 'BioentityListing', element: 'root'}}
        filterSchemas={schemasRegistryFilter}
      />
    </div>
  );
}

export default BioentityListingConnector.connect(BioentityListing);
